import { useEffect, useState } from "react";
import { ActionIcon, Badge, Button, Card, Input } from "@insights/ui";
import { ChevronLeft, ChevronRight, GitBranch, RefreshCw } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router";
import { EmptyState, Loading, PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import { BuildListRows } from "../components/BuildListRows";
import { useBuilds } from "../hooks/useBuilds";
import "../styles/builds.css";

type BuildsPageProps = {
  view?: "latest" | "branch-or-tag";
};

function readExpandedGroupIds(storageKey: string) {
  try {
    const storedGroupIds = JSON.parse(
      sessionStorage.getItem(storageKey) ?? "[]",
    ) as unknown;

    return new Set(
      Array.isArray(storedGroupIds)
        ? storedGroupIds.filter(
            (groupId): groupId is string => typeof groupId === "string",
          )
        : [],
    );
  } catch {
    return new Set<string>();
  }
}

export function BuildsPage({ view = "latest" }: BuildsPageProps) {
  const { workspaceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [repository, setRepository] = useState("");
  const [pageCursors, setPageCursors] = useState<Array<string | undefined>>([
    undefined,
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const groupStateKey = `insights-build-groups:${workspaceId}:${view}`;
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(() =>
    readExpandedGroupIds(groupStateKey),
  );
  const builds = useBuilds(
    workspaceId,
    repository,
    pageCursors[pageIndex],
    view,
  );
  const buildCount = builds.data?.items.length ?? 0;
  const branchOrTagView = view === "branch-or-tag";

  useEffect(() => {
    setExpandedGroupIds(readExpandedGroupIds(groupStateKey));
  }, [groupStateKey]);

  const openBuild = (buildId: string) => {
    navigate(`/w/${workspaceId}/builds/${buildId}`, {
      state: {
        buildModalBackground: location,
        buildsReturnTo: `${location.pathname}${location.search}`,
      },
    });
  };

  const setGroupExpanded = (groupId: string, expanded: boolean) => {
    setExpandedGroupIds((currentGroupIds) => {
      const nextGroupIds = new Set(currentGroupIds);
      if (expanded) {
        nextGroupIds.add(groupId);
      } else {
        nextGroupIds.delete(groupId);
      }
      try {
        sessionStorage.setItem(
          groupStateKey,
          JSON.stringify([...nextGroupIds]),
        );
      } catch {
        // Keep the in-memory interaction working when storage is unavailable.
      }

      return nextGroupIds;
    });
  };

  return (
    <AppShell>
      <PageHeader
        eyebrow="Delivery stream"
        title={branchOrTagView ? "Latest Builds by Branch or Tag" : "Builds"}
      />
      <div className="toolbar">
        <div className="builds-filter-controls">
          <div className="search">
            <GitBranch />
            <Input
              placeholder="Filter repository…"
              value={repository}
              onChange={(event) => {
                setRepository(event.target.value);
                setPageCursors([undefined]);
                setPageIndex(0);
              }}
            />
          </div>
          <ActionIcon
            className="builds-refresh-action"
            disabled={builds.isFetching}
            icon={
              <RefreshCw
                className={builds.isFetching ? "refreshing" : undefined}
              />
            }
            label="Refresh builds"
            onClick={() => void builds.refetch()}
          />
        </div>
        <Badge>
          {buildCount} recent {branchOrTagView ? "branches or tags" : "builds"}
        </Badge>
      </div>
      <Card className="table-card">
        <table className="builds-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Repository</th>
              <th>Environment</th>
              <th>Actor</th>
              <th>Started</th>
            </tr>
          </thead>
          <BuildListRows
            expandedGroupIds={expandedGroupIds}
            items={builds.data?.items ?? []}
            onGroupExpandedChange={setGroupExpanded}
            onOpenBuild={openBuild}
          />
        </table>
        {!builds.isLoading &&
        builds.data?.items.length &&
        (pageIndex > 0 || builds.data.nextCursor) ? (
          <nav aria-label="Build list pagination" className="builds-pagination">
            <Button
              color="neutral"
              disabled={pageIndex === 0}
              icon={<ChevronLeft />}
              onClick={() => {
                setPageIndex((current) => Math.max(0, current - 1));
              }}
              size="small"
              type="button"
            >
              Previous
            </Button>
            <span aria-live="polite">Page {pageIndex + 1}</span>
            <Button
              color="neutral"
              disabled={!builds.data.nextCursor}
              icon={<ChevronRight />}
              iconPosition="end"
              onClick={() => {
                const nextCursor = builds.data.nextCursor;
                if (!nextCursor) {
                  return;
                }

                setPageCursors((current) => [
                  ...current.slice(0, pageIndex + 1),
                  nextCursor,
                ]);
                setPageIndex((current) => current + 1);
              }}
              size="small"
              type="button"
            >
              Next
            </Button>
          </nav>
        ) : null}
        {builds.isLoading && <Loading />}
        {!builds.isLoading && !builds.data?.items.length && (
          <EmptyState
            title="Waiting for the first build"
            text="Point Terrabuild at this Insights API. New builds will appear here immediately."
          />
        )}
      </Card>
    </AppShell>
  );
}

export function BranchOrTagBuildsPage() {
  return <BuildsPage view="branch-or-tag" />;
}
